<template>
  <div v-if="isOpen" class="fixed inset-0 bg-black bg-opacity-80 backdrop-blur-sm flex items-center justify-center p-4 z-50">
    <div class="w-full max-w-lg bg-gray-900 text-white rounded-lg p-6 flex flex-col gap-4">
      <div class="flex justify-between items-center">
        <h3 class="text-xl font-bold text-indigo-300">สร้างลายสักใหม่</h3>
        <button @click="$emit('close')" class="text-gray-400 hover:text-white">
          <i class="fas fa-times text-xl"></i>
        </button>
      </div>

      <form @submit.prevent="createTattoo" class="flex flex-col gap-4">
        <div>
          <label class="text-sm text-gray-300">ชื่อลายสัก</label>
          <input v-model="form.name" type="text" class="w-full bg-gray-800 rounded-lg p-2 text-white" required />
        </div>
        <div>
          <label class="text-sm text-gray-300">URL รูปภาพ</label>
          <input v-model="form.imageUrl" type="url" class="w-full bg-gray-800 rounded-lg p-2 text-white" />
        </div>
        <div>
          <label class="text-sm text-gray-300">ประเภทลายสัก</label>
          <select v-model="form.type" class="w-full bg-gray-800 rounded-lg p-2 text-white" required>
            <option value="str">STR</option>
            <option value="stamina">Stamina</option>
            <option value="int">INT</option>
            <option value="agi">AGI</option>
            <option value="luck">LUCK</option>
          </select>
        </div>
        <div>
          <label class="text-sm text-gray-300">ลำดับ (Order)</label>
          <input v-model.number="form.order" type="number" min="0" class="w-full bg-gray-800 rounded-lg p-2 text-white" required />
        </div>
        <div>
          <label class="text-sm text-gray-300">ระดับที่ต้องการ (Level Required)</label>
          <input v-model.number="form.levelRequired" type="number" min="0" class="w-full bg-gray-800 rounded-lg p-2 text-white" required />
        </div>
        <div class="grid grid-cols-2 gap-2">
          <div>
            <label class="text-sm text-gray-300">ราคา (Cash)</label>
            <input v-model.number="form.cost.cash" type="number" min="0" class="w-full bg-gray-800 rounded-lg p-2 text-white" required />
          </div>
          <div>
            <label class="text-sm text-gray-300">ราคา (Diamond)</label>
            <input v-model.number="form.cost.diamond" type="number" min="0" class="w-full bg-gray-800 rounded-lg p-2 text-white" required />
          </div>
          <div>
            <label class="text-sm text-gray-300">ราคา (Gold)</label>
            <input v-model.number="form.cost.gold" type="number" min="0" class="w-full bg-gray-800 rounded-lg p-2 text-white" required />
          </div>
          <div>
            <label class="text-sm text-gray-300">ราคา (Silver)</label>
            <input v-model.number="form.cost.silver" type="number" min="0" class="w-full bg-gray-800 rounded-lg p-2 text-white" required />
          </div>
        </div>
        <div>
          <label class="text-sm text-gray-300 font-bold">สถานะ</label>
          <div class="grid grid-cols-3 gap-2 mt-2">
            <div>
              <label class="text-sm text-gray-300">STR</label>
              <input v-model.number="form.stats.str" type="number" min="0" class="w-full bg-gray-800 rounded-lg p-2 text-white" required />
            </div>
            <div>
              <label class="text-sm text-gray-300">Stamina</label>
              <input v-model.number="form.stats.stamina" type="number" min="0" class="w-full bg-gray-800 rounded-lg p-2 text-white" required />
            </div>
            <div>
              <label class="text-sm text-gray-300">INT</label>
              <input v-model.number="form.stats.int" type="number" min="0" class="w-full bg-gray-800 rounded-lg p-2 text-white" required />
            </div>
            <div>
              <label class="text-sm text-gray-300">AGI</label>
              <input v-model.number="form.stats.agi" type="number" min="0" class="w-full bg-gray-800 rounded-lg p-2 text-white" required />
            </div>
            <div>
              <label class="text-sm text-gray-300">LUCK</label>
              <input v-model.number="form.stats.luck" type="number" min="0" class="w-full bg-gray-800 rounded-lg p-2 text-white" required />
            </div>
          </div>
        </div>
        <button 
          type="submit" 
          class="bg-indigo-600 hover:bg-indigo-700 text-white py-2 px-4 rounded-lg flex items-center justify-center gap-2"
          :disabled="isCreating"
        >
          <span v-if="!isCreating"><i class="fas fa-plus mr-2"></i> สร้างลายสัก</span>
          <div v-else class="animate-spin rounded-full h-5 w-5 border-t-2 border-white"></div>
        </button>
      </form>

      <p v-if="error" class="text-red-500 text-sm text-center">{{ error }}</p>
    </div>
  </div>
</template>

<script>
import { ref } from 'vue';
import { projectFirestore } from '@/firebase/config';

export default {
  name: 'CreateTattooModal',
  props: {
    isOpen: Boolean
  },
  emits: ['close', 'tattoo-created'],
  setup(props, { emit }) {
    const form = ref({
      name: '',
      imageUrl: '',
      type: 'str',
      order: 0,
      levelRequired: 0,
      cost: { cash: 0, diamond: 0, gold: 0, silver: 0 },
      stats: { str: 0, stamina: 0, int: 0, agi: 0, luck: 0 }
    });
    const isCreating = ref(false);
    const error = ref('');

    const createTattoo = async () => {
      isCreating.value = true;
      error.value = '';
      try {
        await projectFirestore.collection('tattoos').doc(form.value.name).set({
          name: form.value.name,
          imageUrl: form.value.imageUrl || 'default-tattoo.png',
          type: form.value.type,
          order: form.value.order,
          levelRequired: form.value.levelRequired,
          cost: form.value.cost,
          stats: form.value.stats
        });
        emit('tattoo-created');
        emit('close');
        form.value = {
          name: '',
          imageUrl: '',
          type: 'str',
          order: 0,
          levelRequired: 0,
          cost: { cash: 0, diamond: 0, gold: 0, silver: 0 },
          stats: { str: 0, stamina: 0, int: 0, agi: 0, luck: 0 }
        };
      } catch (err) {
        console.error('Error creating tattoo:', err);
        error.value = 'เกิดข้อผิดพลาดในการสร้างลายสัก';
      } finally {
        isCreating.value = false;
      }
    };

    return { form, isCreating, error, createTattoo };
  }
};
</script>